'use client'

import { useEffect } from 'react'
import {
  useWatch,
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormReturn,
} from 'react-hook-form'
import { useCepLookup } from '@/hooks/useCepLookup'
import { IBGE_STATES, IBGE_MUNICIPALITIES, type IbgeMunicipality } from '@/data/ibge'

/**
 * Shape mínimo que um formulário precisa expor para reutilizar a lógica de
 * autopreenchimento de endereço via CEP + sincronização dos selects de
 * UF/Cidade do IBGE. Qualquer formulário (cadastro do CFC, registro, etc.)
 * que declare estes campos pode reaproveitar o hook sem duplicar a lógica.
 */
export interface IbgeAddressFormValues extends FieldValues {
  uf_id: string
  city_id: string
  address: {
    postal_code: string
    street: string
    number: string
    complement?: string
    neighborhood: string
    city: string
    state: string
  }
}

export interface UseIbgeCepAutofillResult {
  /** Estado da busca de CEP (loading, endereço, notFound, error, search()). */
  cepLookup: ReturnType<typeof useCepLookup>
  /** UF selecionada (string do id do IBGE) ou vazio. */
  ufId: string
  /** Municípios da UF selecionada. */
  municipalities: IbgeMunicipality[]
  /** Handler para o select de UF (reseta cidade e espelha o endereço). */
  handleUfChange: (value: string) => void
  /** Handler para o select de Cidade (espelha nome da cidade e UF). */
  handleCityChange: (value: string) => void
}

/**
 * Centraliza toda a lógica de endereço baseada em IBGE + ViaCEP compartilhada
 * entre formulários. Mantém o hook desacoplado do JSX: o formulário apenas
 * consome os handlers/estado retornados e renderiza os campos como preferir.
 */
export function useIbgeCepAutofill<T extends IbgeAddressFormValues>(
  form: UseFormReturn<T>,
): UseIbgeCepAutofillResult {
  const { control } = form

  // Helpers para acessar os campos fixos com segurança de tipos do RHF.
  const path = (name: string) => name as Path<T>
  const setField = (name: string, value: unknown, shouldValidate = true) =>
    form.setValue(path(name), value as PathValue<T, Path<T>>, { shouldValidate })

  const ufId = (useWatch({ control, name: path('uf_id') }) ?? '') as string
  const postalCode = (useWatch({ control, name: path('address.postal_code') }) ?? '') as string
  const cepLookup = useCepLookup(postalCode)

  const municipalities = ufId ? IBGE_MUNICIPALITIES[Number(ufId)] ?? [] : []

  useEffect(() => {
    if (cepLookup.isLoading) {
      form.clearErrors(path('address.postal_code'))
      return
    }

    if (cepLookup.notFound) {
      form.setError(path('address.postal_code'), { type: 'manual', message: 'CEP não encontrado' })
      return
    }

    if (cepLookup.error) {
      form.setError(path('address.postal_code'), { type: 'manual', message: cepLookup.error })
      return
    }

    if (cepLookup.address) {
      const { street, neighborhood, city, state: stateUF, complement } = cepLookup.address
      setField('address.street', street)
      setField('address.neighborhood', neighborhood)
      setField('address.city', city)
      setField('address.state', stateUF)
      if (!form.getValues(path('address.complement'))) {
        setField('address.complement', complement ?? undefined)
      }

      const stateMatch = IBGE_STATES.find((state) => state.uf === stateUF)
      if (stateMatch) {
        setField('uf_id', String(stateMatch.id))
        const municipality = IBGE_MUNICIPALITIES[stateMatch.id]?.find((c) => c.name === city)
        setField('city_id', municipality ? String(municipality.id) : '')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cepLookup, form])

  function handleUfChange(value: string) {
    form.setValue(path('uf_id'), value as PathValue<T, Path<T>>, {
      shouldValidate: true,
      shouldDirty: true,
    })
    form.setValue(path('city_id'), '' as PathValue<T, Path<T>>, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setField('address.city', '')
    setField('address.state', '')
  }

  function handleCityChange(value: string) {
    form.setValue(path('city_id'), value as PathValue<T, Path<T>>, {
      shouldValidate: true,
      shouldDirty: true,
    })
    const city = municipalities.find((c) => c.id === Number(value))
    if (city) {
      setField('address.city', city.name)
      const state = IBGE_STATES.find((s) => s.id === Number(ufId))
      setField('address.state', state?.uf ?? '')
    }
  }

  return { cepLookup, ufId, municipalities, handleUfChange, handleCityChange }
}
